import { cloneDeep, get, set } from 'lodash';
import { ANTRAGS_FORMULAREN, KOSTEN_ARTEN } from 'src/lib/constants/constant';
import { isTrue } from 'src/utils/helper';
import { executeEpzMAFormulas } from './executeEpzMAFormulas';
import { executeKreativMAFormulas } from './kreativ/executeKreativMAFormulas';

// import schemas
import MittelabrufSchema from 'src/lib/paths/mittelabruf_FULL_Paths.json';
import NachweisPositionSchema from 'src/lib/paths/nachweisposition_FULL_Paths.json';
import { executeStipendiumMAFormulas } from './stipendium/executeStipendiumMAFormulas';
import { executeLoeweMAFormulas } from './loewe/executeLoeweMAFormulas';
const MA_P = MittelabrufSchema.attributes;
const NP_P = NachweisPositionSchema.attributes;

export const executeMittelabrufFormulas = (application: any) => {
	const antragsformular = get(application, MA_P.Foerderantrag.schema.attributes.Allgemein_Antragsformular.path, '');
	switch (antragsformular) {
		case ANTRAGS_FORMULAREN.Entwicklungszusammenarbeit:
			return executeEpzMAFormulas(application);
		case ANTRAGS_FORMULAREN.Kreativwirtschaft:
			return executeKreativMAFormulas(application);
		case ANTRAGS_FORMULAREN.StartUpStipendium:
			return executeStipendiumMAFormulas(application);
		case ANTRAGS_FORMULAREN.Loewe:
			return executeLoeweMAFormulas(application);
		default:
			return executeDefaultMittelabrufFormulas(application);
	}
};

const executeDefaultMittelabrufFormulas = (application: any) => {
	let result: any = [];
	let appCopy = cloneDeep(application);

	const kalkulationBlock = get(appCopy, MA_P.Kalkulation_Block_1.path, []);

	const calculateKalkulationBlock_New = transformKalkulationBlock(kalkulationBlock);

	const getaetigteAusgabeGesamt = calculateKalkulationBlock_New.reduce((sum: any, ele: any) => sum + parseFloat(get(ele, 'Zelle_3', '0')), 0);
	const zuwendungAnteilingGesamt = calculateKalkulationBlock_New.reduce((sum: any, ele: any) => sum + parseFloat(get(ele, 'Zelle_4', '0')), 0);

	const IndividuelleFoerderquoteGesamt = (zuwendungAnteilingGesamt / getaetigteAusgabeGesamt) * 100;

	const Anforderungsbetrag =
		zuwendungAnteilingGesamt - parseFloat(get(appCopy, MA_P.Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr.path, '0'));

	let calculatedSachKosten = calculateSachkosten(appCopy);
	result.push(...calculatedSachKosten);
	for (let ele of calculatedSachKosten) {
		set(appCopy, get(ele, 'path'), get(ele, 'value'));
	}

	result.push(
		{
			path: MA_P.Kalkulation_Block_1.path,
			value: calculateKalkulationBlock_New,
		},
		{
			path: MA_P.Individuelle_Foerderquote_Gesamt.path,
			value: !isNaN(IndividuelleFoerderquoteGesamt) ? IndividuelleFoerderquoteGesamt.toFixed(2) : 0,
		},
		{
			path: MA_P.Tatsaechlich_Getaetigte_Ausgaben_Gesamt.path,
			value: !isNaN(getaetigteAusgabeGesamt) ? getaetigteAusgabeGesamt.toFixed(2) : 0,
		},
		{
			path: MA_P.Zuwendung_Anteilig_Gesamt.path,
			value: !isNaN(zuwendungAnteilingGesamt) ? zuwendungAnteilingGesamt.toFixed(2) : 0,
		},

		{
			path: MA_P.Ermittlung_Des_Auszahlungsbetrages_Zuwendungsfaehige_Ausgaben.path,
			value: !isNaN(getaetigteAusgabeGesamt) ? getaetigteAusgabeGesamt.toFixed(2) : 0,
		},
		{
			path: MA_P.Ermittlung_Des_Auszahlungsbetrages_Zuwendung_Anteilig.path,
			value: !isNaN(zuwendungAnteilingGesamt) ? zuwendungAnteilingGesamt.toFixed(2) : 0,
		},
		{
			path: MA_P.Ermittlung_Des_Auszahlungsbetrages_Anforderungsbetrag.path,
			value: !isNaN(Anforderungsbetrag) ? Anforderungsbetrag.toFixed(2) : 0,
		},
	);
};

function transformKalkulationBlock(data: any[]) {
	return data.map((eachEle: any) => {
		const foerderQuote = get(eachEle, 'Zelle_2', 0);
		const getaetigteAusgabe = get(eachEle, 'Zelle_3', '0');
		const zuwendungAnteilig = getaetigteAusgabe > 0 ? (getaetigteAusgabe * foerderQuote) / 100 : 0;

		return {
			...eachEle,
			Zelle_4: zuwendungAnteilig.toFixed(2),
		};
	});
}

function calculateSachkosten(application: any) {
	let result = [];
	const allNachweisPositions = get(application, MA_P.Mittelabruf_Nachweispositions.path, []);
	for (let i = 0; i < allNachweisPositions.length; i++) {
		const eachNachweis = allNachweisPositions[i];

		if (get(eachNachweis, NP_P.Allgemein_Kostenposition.schema.attributes.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Sachkosten) {
			const KalkulationSachKosten = transformSachkostenData(eachNachweis);

			result.push({
				path: `${MA_P.Mittelabruf_Nachweispositions.path}.${i}`,
				value: KalkulationSachKosten,
			});
		}
	}

	return result;
}
function transformSachkostenData(eachNachweisData: any) {
	const investition = get(eachNachweisData, NP_P.Beleg_Af_A_Absetzung_Fuer_Abnutzungen_Investition.path, 0);
	const abschreibunsMonate = get(eachNachweisData, NP_P.Beleg_Af_A_Absetzung_Fuer_Abnutzungen_Abschreibungsmonate.path, 0);
	const nutzungsMonate = get(eachNachweisData, NP_P.Beleg_Af_A_Absetzung_Fue_0114037618_m_Betrachteten_Haushaltsjahr.path, 0);
	const projektbezogeneNutzung = get(eachNachweisData, NP_P.Beleg_Af_A_Absetzung_Fue_0188964903_ezogeneggf_Anteilige_Nutzung.path, 0);

	let ansetzbarerBetrag: number = 0.0;

	if (isTrue(get(eachNachweisData, NP_P.Beleg_Af_A_Absetzung_Fuer_Abnutzungen_Abschreibungsfaehig.path))) {
		ansetzbarerBetrag = (investition / abschreibunsMonate) * (nutzungsMonate * (projektbezogeneNutzung / 100));
	} else {
		ansetzbarerBetrag = parseFloat(get(eachNachweisData, NP_P.Allgemein_Ausgaben_Kosten.path, '0.0'));
	}

	return {
		...eachNachweisData,
		attributes: {
			...get(eachNachweisData, 'attributes', {}),
			Beleg_Af_A_Absetzung_Fue_0246879351_etzbarer_Abschreibungsbetrag: !isNaN(ansetzbarerBetrag) ? ansetzbarerBetrag.toFixed(2) : 0,
			Allgemein_Ausgaben_Kosten: !isNaN(ansetzbarerBetrag) ? ansetzbarerBetrag.toFixed(2) : 0,
		},
	};
}
